import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, X, Check } from 'lucide-react';
import { readSession, writeSession } from '../lib/storage';

const ACK_KEY = 'fb_safety_ack_v1';

/** Has the user accepted the safety acknowledgement this session? */
export const hasSafetyAck = () => readSession(ACK_KEY, null) === 'accepted';

/** Imperatively mark the safety acknowledgement as accepted. */
const markAccepted = () => writeSession(ACK_KEY, 'accepted');

// Each item must be ticked before the user can proceed. Order matters for visual flow.
const ITEMS = [
  { id: 'age', label: <>I am <b>18 years or older</b>.</> },
  { id: 'dose', label: <>I acknowledge the recommended <b>starting dose is 10mg = 4× 2.5mg per week</b>, with a <b>minimum 2 months between dose increases</b>.</> },
  { id: 'max', label: <>I will <b>not exceed 15mg per week</b> under any circumstances.</> },
  { id: 'storage', label: <>Once reconstituted I will store the product <b>refrigerated (2–8&deg;C)</b> and discard after <b>28–32 days</b>.</> },
  { id: 'risk', label: <>I understand there are <b>no medical guarantees</b> and I assume all risk associated with use.</> },
  { id: 'no_advice', label: <>I understand FAT BUSTERS <b>does not provide medical advice</b> and recommends consulting a licensed practitioner.</> },
  { id: 'read', label: <>I have read the <Link to="/dosing" target="_blank" rel="noopener" className="text-red-600 underline font-semibold">Dosing Guide</Link>.</> },
];

const SafetyAckModal = ({ open, onAccept, onCancel }) => {
  const [checked, setChecked] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const allChecked = ITEMS.every((it) => checked[it.id]);
  const toggle = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));

  const accept = () => {
    if (!allChecked) return;
    markAccepted();
    setChecked({});
    onAccept?.();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="safety-ack-modal">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <header className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={20} className="text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[#0a0a0a]">Safety acknowledgement</h2>
            <p className="text-xs text-slate-500 mt-0.5">Please confirm you understand the dosing &amp; storage requirements before adding products to your cart.</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="p-2 text-slate-400 hover:text-slate-700 rounded-lg flex-shrink-0"
            data-testid="safety-ack-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="overflow-y-auto px-5 sm:px-6 py-4">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 mb-4">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>Tick each box below. Need a refresher? Open the <Link to="/dosing" target="_blank" rel="noopener" className="underline font-semibold">full dosing guide</Link>.</span>
          </div>

          <ul className="space-y-2.5">
            {ITEMS.map((it) => (
              <li key={it.id}>
                <label
                  className={`flex items-start gap-3 px-3.5 py-3 rounded-lg border cursor-pointer transition-colors ${
                    checked[it.id]
                      ? 'bg-emerald-50 border-emerald-300'
                      : 'bg-white border-slate-200 hover:border-red-300'
                  }`}
                  data-testid={`safety-ack-item-${it.id}`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 accent-red-600 flex-shrink-0"
                    checked={!!checked[it.id]}
                    onChange={() => toggle(it.id)}
                  />
                  <span className="text-sm text-[#0a0a0a] leading-snug">{it.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        <footer className="px-5 sm:px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl sm:rounded-b-2xl">
          <button
            type="button"
            onClick={accept}
            disabled={!allChecked}
            data-testid="safety-ack-accept"
            className={`w-full font-bold rounded-xl px-5 py-3.5 inline-flex items-center justify-center gap-2 transition-colors ${
              allChecked
                ? 'bg-red-500 hover:bg-red-400 text-white'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <Check size={16} />
            {allChecked ? 'I accept — continue shopping' : `Tick all ${ITEMS.length} boxes to continue`}
          </button>
          <p className="text-[11px] text-slate-500 mt-2 text-center">
            Your acknowledgement is remembered for this session.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default SafetyAckModal;
